import { AppRole } from '@/constants/roles';

export interface ServiceRecord {
  date: string;
  metro: number;
  laundry: number;
  meal: number;
  juice: number;
}

// Child interface for family shelters
export interface FamilyChild {
  name: string;
  age?: number;
  dateOfBirth?: string;
}

export interface Client {
  id: string;
  name: string;
  age: number;
  room?: string;
  room_id?: string;
  site_id?: string;
  site_name?: string;
  siteName?: string;
  status: 'checked-in' | 'checked-out' | 'unbedded' | 'not_present' | 'reserved' | 'waitlist';
  caseWorker: string;
  case_worker_id?: string;
  phone?: string;
  email?: string;
  address?: string;
  
  // allergies removed — Cimfoni stores no PHI
  alerts?: string[];
  checkInTime?: string;
  checkOutTime?: string;
  lastActivity?: string;
  photo?: string;
  emergencyContact?: {
    name: string;
    relationship: string;
    phone: string;
  };
  services?: {
    daily: ServiceRecord;
    total: ServiceRecord;
  };
  isReturnee?: boolean;
  returneeObservations?: string;
  passStart?: string;
  passEnd?: string;
  passType?: string;
  // Family shelter fields
  isHeadOfHousehold?: boolean;
  partnerId?: string;
  partnerName?: string;
  children?: FamilyChild[];
  propertyStorageNotes?: string;
}

// Helper function to get display status
export const getDisplayStatus = (client: Client): { status: string; isOnPass: boolean; passEnd?: string } => {
  // Check if client has an active pass
  if (client.passStart && client.passEnd) {
    const now = new Date();
    const passStart = new Date(client.passStart);
    const passEnd = new Date(client.passEnd);
    if (now >= passStart && now <= passEnd) {
      return { status: 'Pass', isOnPass: true, passEnd: client.passEnd };
    }
  }
  
  // Map status to display text
  if (client.status === 'checked-in') return { status: 'Checked In', isOnPass: false };
  if (client.status === 'checked-out') return { status: 'Checked Out', isOnPass: false };
  if (client.status === 'not_present') return { status: 'Not Present', isOnPass: false };
  if (client.status === 'unbedded') return { status: 'Unbedded', isOnPass: false };
  if (client.status === 'reserved') return { status: 'Reserved', isOnPass: false };
  if (client.status === 'waitlist') return { status: 'Wait List', isOnPass: false };
  // hospital status removed — no PHI
  if ((client.status as string) === 'pass') return { status: 'Pass', isOnPass: true, passEnd: client.passEnd };
  return { status: 'Unknown', isOnPass: false };
};

export interface User {
  id: string;
  username: string;
  role: AppRole;
  name: string;
}

export interface AlertAcknowledgement {
  id: string;
  alertId: string;
  userId: string;
  acknowledgedAt: string;
  userName?: string;
}

export interface Alert {
  id: string;
  type: 'critical' | 'warning' | 'info';
  title: string;
  description: string;
  timestamp: string;
  status: 'active' | 'resolved' | 'archived';
  createdBy?: string;
  acknowledgedBy?: string;
  clientId?: string;
  category?: 'client' | 'staff' | 'service' | 'building' | 'system';
  acknowledgements?: AlertAcknowledgement[];
}

export interface SystemMetrics {
  cpu: number;
  memory: number;
  network: number;
  database: number;
}

export interface RosterClient {
  clientId: string;
  name: string;
  room: string;
  subRoom?: string; // For 103a, 103b designations
  hasAssignedRoom: boolean;
}